import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type VehicleState } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';

const VEHICLES = 'vehicles';

const converter = buildConverter<VehicleState>(
  (data) =>
    ({
      ...data,
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
    }) as VehicleState,
  (model) => ({ ...model, updatedAt: serverTimestamp() })
);

export class FirestoreVehicleRepository {
  async findAll(): Promise<VehicleState[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(collection(db, VEHICLES), where('environment', '==', getCurrentEnvironment())).withConverter(
      converter
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<VehicleState | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, VEHICLES, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<VehicleState[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, VEHICLES),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(vehicle: VehicleState): Promise<VehicleState> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const existing = await this.findById(vehicle.id);
    const data = { ...vehicle, environment: getCurrentEnvironment() };
    if (existing) {
      await updateDoc(doc(db, VEHICLES, vehicle.id), { ...data, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, VEHICLES, vehicle.id).withConverter(converter), data);
    }
    return { ...data, updatedAt: new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, VEHICLES, id));
  }
}

export const createFirestoreVehicleRepository = (): FirestoreVehicleRepository => new FirestoreVehicleRepository();
